// client/src/pages/Admin.tsx
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
LayoutDashboard,
Mail,
MessageSquare,
FolderOpen,
BookOpen,
HandCoins,
LogOut,
RefreshCw,
Users,
Bell,
Settings,
ChevronDown,
Search,
Filter,
PlusCircle,
Eye,
EyeOff,
Edit,
Trash2,
MoreVertical,
Download,
Upload,
BarChart3,
PieChart,
TrendingUp,
Calendar,
Clock,
CheckCircle,
AlertCircle,
XCircle,
HelpCircle,
User,
Shield,
Globe,
Zap,
Award,
Target,
Heart,
LogIn,
Loader2,
LockKeyhole,
ShieldCheck,
HeartHandshake,
CheckCircle2,
ArrowRight,
Activity,
KeyRound,
X,
Moon,
Sun,
Monitor,
Palette,
Save,
UserCircle,
AtSign,
Phone,
Smartphone,
Fingerprint,
Shield as ShieldIcon,
Lock,
Unlock,
Eye as EyeIcon,
EyeOff as EyeOffIcon,
AlertTriangle,
Info,
Check,
ChevronRight,
ChevronLeft,
Mail as MailIcon,
BellRing,
Gift,
MessageSquare as MessageSquareIcon,
Clipboard
} from 'lucide-react';
import { ResourceManager } from './Admin/ResourceManager';
import { ProjectManager } from './Admin/ProjectManager';
import { Donations } from './Admin/Donations';
import { ActivityLog } from './Admin/ActivityLog/ActivityLog';
import ErrorBoundary from '../components/ErrorBoundary';
import {
  loginSchema,
  type LoginFormData
} from './Admin/schemas/adminAuth.schema';
import { useAuth } from '../context/AuthContext';
import { apiClient } from '../api/client';
import './Admin.css';

interface Contact {
_id: string;
name: string;
email: string;
message: string;
status: 'new' | 'read' | 'replied' | 'archived';
createdAt: string;
updatedAt: string;
}

// ============================================
// SETTINGS TYPES
// ============================================
interface UserProfile {
name: string;
email: string;
phone: string;
avatar?: string;
}

interface SecuritySettings {
twoFactorEnabled: boolean;
sessionTimeout: number;
ipWhitelist: string[];
lastLogin: string;
loginHistory: Array<{
date: string;
ip: string;
device: string;
}>;
}

interface NotificationPreferences {
emailAlerts: boolean;
systemNotifications: boolean;
donationAlerts: boolean;
contactAlerts: boolean;
projectUpdates: boolean;
}

interface ThemeSettings {
mode: 'light' | 'dark' | 'system';
primaryColor: string;
accentColor: string;
}

interface ResetData {
username: string;
token: string;
newPassword: string;
confirmPassword: string;
}

const Admin: React.FC = () => {
// ==========================================
// AUTH CONTEXT
// ==========================================
const { 
  user, 
  token, 
  login, 
  logout, 
  isAuthenticated, 
  isAdmin, 
  isSuperAdmin 
} = useAuth();

// ==========================================
// STATE
// ==========================================
const [contacts, setContacts] = useState<Contact[]>([]);
const [loading, setLoading] = useState(false);
const [error, setError] = useState('');
const [resetData, setResetData] = useState<ResetData>({
username: '',
token: '',
newPassword: '',
confirmPassword: ''
});
const [showPassword, setShowPassword] = useState(false);
const [rememberMe, setRememberMe] = useState(true);
const [authMode, setAuthMode] = useState<'login' | 'request-reset' | 'reset'>('login');
const [resetSuccess, setResetSuccess] = useState('');
const [filter, setFilter] = useState('all');
const [activeTab, setActiveTab] = useState<
'contacts' | 'resources' | 'projects' | 'donations' | 'activity' | 'settings'
>('contacts');

// ==========================================
// 2FA LOGIN STATE
// ==========================================
const [is2FARequired, setIs2FARequired] = useState(false);
const [show2FAVerification, setShow2FAVerification] = useState(false);
const [twoFACode, setTwoFACode] = useState('');
const [twoFAMode, setTwoFAMode] = useState<'login' | 'setup' | 'disable' | null>(null);
const [twoFASetup, setTwoFASetup] = useState<{
    qrCode: string;
    secret: string;
} | null>(null);

// ==========================================
// CHANGE PASSWORD STATE
// ==========================================
const [currentPassword, setCurrentPassword] = useState('');
const [newPassword, setNewPassword] = useState('');
const [confirmPassword, setConfirmPassword] = useState('');
const [changePasswordError, setChangePasswordError] = useState('');
const [changePasswordSuccess, setChangePasswordSuccess] = useState('');
const [isChangingPassword, setIsChangingPassword] = useState(false);
const [showCurrentPassword, setShowCurrentPassword] = useState(false);
const [showNewPassword, setShowNewPassword] = useState(false);
const [showConfirmPassword, setShowConfirmPassword] = useState(false);

// ==========================================
// SETTINGS STATE
// ==========================================
const [userProfile, setUserProfile] = useState<UserProfile>({
name: user?.username || '',
email: user?.email || '',
phone: ''
});
const [securitySettings, setSecuritySettings] = useState<SecuritySettings>({
twoFactorEnabled: false,
sessionTimeout: 30,
ipWhitelist: [],
lastLogin: 'No login recorded',
loginHistory: []
});
const [notificationPreferences, setNotificationPreferences] = useState<NotificationPreferences>({
emailAlerts: true,
systemNotifications: true,
donationAlerts: true,
contactAlerts: true,
projectUpdates: true
});
const [themeSettings, setThemeSettings] = useState<ThemeSettings>({
mode: 'light',
primaryColor: '#ed1c24',
accentColor: '#afe11a'
});
const [settingsTab, setSettingsTab] = useState<
'profile' | 'security' | 'notifications' | 'theme'
>('profile');
const [settingsSaved, setSettingsSaved] = useState(false);
const [settingsLoading, setSettingsLoading] = useState(false);
const [settingsSaving, setSettingsSaving] = useState(false);
const [ipInput, setIpInput] = useState('');

// ==========================================
// LOGIN FORM
// ==========================================
const {
  register,
  handleSubmit,
  getValues,
  reset,
  formState: { errors: loginFormErrors }
} = useForm<LoginFormData>({
  resolver: zodResolver(loginSchema),
  defaultValues: {
    username: '',
    password: ''
  },
  mode: 'onSubmit'
});

// ==========================================
// ACTIVITY LOGGING
// ==========================================
const logActivity = async (data: any) => {
  const authToken = localStorage.getItem('adminToken');

  if (!authToken) {
    return;
  }

  try {
    await fetch(
      '/.netlify/functions/admin-log-activity',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(data)
      }
    );
  } catch (error) {
    console.error('Error logging activity:', error);
  }
};

// ==========================================
// SESSION TIMEOUT
// ==========================================
useEffect(() => {
let inactivityTimer: ReturnType<typeof setTimeout>;
if (isAuthenticated) {
const resetInactivityTimer = () => {
clearTimeout(inactivityTimer);
inactivityTimer = setTimeout(() => {
handleLogout();
alert('Session expired due to inactivity. Please login again.');
}, securitySettings.sessionTimeout * 60 * 1000);
};
const events = ['click', 'keydown', 'scroll', 'mousemove'];
events.forEach(event => {
window.addEventListener(event, resetInactivityTimer);
});
resetInactivityTimer();
return () => {
clearTimeout(inactivityTimer);
events.forEach(event => {
window.removeEventListener(event, resetInactivityTimer);
});
};
}
}, [isAuthenticated, securitySettings.sessionTimeout]);

// ==========================================
// AUTHENTICATION / PASSWORD RESET LINK
// ==========================================
useEffect(() => {
if (token) {
void fetchContacts();
void fetchSettings();
}

const resetToken = new URLSearchParams(window.location.search).get('token');
if (resetToken && resetToken.trim()) {
setResetData((current) => ({
...current,
token: resetToken.trim()
}));
setAuthMode('reset');
setError('');
setResetSuccess('');
}
}, [token]);

// ==========================================
// LOGIN HANDLER
// ==========================================
const handleLogin = async (credentialsData: LoginFormData) => {
setLoading(true);
setError('');
setResetSuccess('');
setIs2FARequired(false);

try {
const response = await fetch(
'/.netlify/functions/admin-auth',
{
method: 'POST',
headers: {
'Content-Type': 'application/json'
},
body: JSON.stringify({
username: credentialsData.username,
password: credentialsData.password
})
}
);
const data = await response.json();

if (data.requires2FA === true) {
setIs2FARequired(true);
setTwoFAMode('login');
setShow2FAVerification(true);
setTwoFACode('');
setError('');
setLoading(false);
return;
}

if (!response.ok || !data.success) {
throw new Error(data.error || 'Invalid credentials');
}

// Store the token FIRST
login(data.token, data.user);
setError('');

// Wait a moment for the token to be set in AuthContext state
await new Promise(resolve => setTimeout(resolve, 150));

// Now fetch contacts and settings with the new token
await fetchContacts();
await fetchSettings();

// Log login activity
await logActivity({
action: 'login',
severity: 'info',
details: {
description: `User ${credentialsData.username} logged in successfully`
}
});

} catch (err) {
setError(err instanceof Error ? err.message : 'Login failed');
} finally {
setLoading(false);
}
};

// ==========================================
// 2FA LOGIN VERIFICATION
// ==========================================
const handleVerify2FALogin = async () => {
if (!twoFACode.trim()) {
setError('Enter the 6-digit authenticator code.');
return;
}

setLoading(true);
setError('');

try {
const response = await fetch(
'/.netlify/functions/admin-auth',
{
method: 'POST',
headers: {
'Content-Type': 'application/json'
},
body: JSON.stringify({
username: getValues('username'),
password: getValues('password'),
twoFactorCode: twoFACode.trim()
})
}
);

const data = await response.json();

if (data.requires2FA === true) {
setError('A valid 6-digit authenticator code is required.');
return;
}

if (!response.ok || !data.success || !data.token) {
throw new Error(data.error || 'Invalid 2FA code');
}

login(data.token, data.user);
setShow2FAVerification(false);
setIs2FARequired(false);
setTwoFAMode(null);
setError('');
setTwoFACode('');

// Wait a moment for the token to be set in AuthContext state
await new Promise(resolve => setTimeout(resolve, 150));

await fetchContacts();
await fetchSettings();

await logActivity({
action: 'login_2fa',
severity: 'info',
details: {
description: `User ${getValues('username')} logged in successfully with 2FA`
}
});

} catch (err) {
setError(err instanceof Error ? err.message : 'Failed to verify 2FA code');
} finally {
setLoading(false);
}
};

// ==========================================
// PASSWORD RESET REQUEST
// ==========================================
const handlePasswordResetRequest = async (e: React.FormEvent) => {
e.preventDefault();
setLoading(true);
setError('');
setResetSuccess('');

const username = resetData.username.trim();
if (!username) {
setError('Please enter your username.');
setLoading(false);
return;
}

try {
const response = await fetch(
'/.netlify/functions/admin-password-reset-request',
{
method: 'POST',
headers: {
'Content-Type': 'application/json'
},
body: JSON.stringify({ username })
}
);

const data = await response.json();
if (!response.ok || !data.success) {
throw new Error(data.error || 'Unable to process the password reset request');
}

setResetSuccess(
'If an administrator account exists for that username, a password reset link has been sent to its registered email address. Please check your email.'
);
setResetData((current) => ({
...current,
username: ''
}));

await logActivity({
action: 'password_reset_requested',
severity: 'info',
details: {
description: `Password reset requested for username: ${username}`
}
});

} catch (err) {
setError(err instanceof Error ? err.message : 'Unable to request a password reset');
} finally {
setLoading(false);
}
};

// ==========================================
// PASSWORD RESET
// ==========================================
const handlePasswordReset = async (e: React.FormEvent) => {
e.preventDefault();
setLoading(true);
setError('');
setResetSuccess('');

const resetToken = resetData.token.trim();
if (!resetToken) {
setError('This password reset link is missing or invalid. Please request a new reset link.');
setLoading(false);
return;
}

if (resetData.newPassword.length < 8) {
setError('Password must be at least 8 characters long.');
setLoading(false);
return;
}

if (resetData.newPassword !== resetData.confirmPassword) {
setError('New passwords do not match.');
setLoading(false);
return;
}

try {
const response = await fetch(
'/.netlify/functions/admin-password-reset',
{
method: 'POST',
headers: {
'Content-Type': 'application/json'
},
body: JSON.stringify({
token: resetToken,
newPassword: resetData.newPassword
})
}
);

const data = await response.json();
if (!response.ok || !data.success) {
throw new Error(data.error || 'Password reset failed');
}

setResetSuccess('Password updated successfully. You can now sign in with your new password.');
const username = resetData.username || 'unknown';
setResetData({
username: '',
token: '',
newPassword: '',
confirmPassword: ''
});
reset();
setAuthMode('login');

window.history.replaceState({}, '', window.location.pathname);

await logActivity({
action: 'password_reset_completed',
severity: 'info',
details: {
description: 'Password reset completed successfully'
}
});

} catch (err) {
setError(err instanceof Error ? err.message : 'Password reset failed');
} finally {
setLoading(false);
}
};

// ==========================================
// LOGOUT
// ==========================================
const handleLogout = () => {
logActivity({
action: 'logout',
severity: 'info',
details: {
description: 'User logged out'
}
});

logout();
setIs2FARequired(false);
setShow2FAVerification(false);
setTwoFAMode(null);
setTwoFACode('');
setTwoFASetup(null);
setContacts([]);
};

// ==========================================
// CHANGE PASSWORD HANDLER
// ==========================================
const handleChangePassword = async (e: React.FormEvent) => {
e.preventDefault();
setChangePasswordError('');
setChangePasswordSuccess('');
setIsChangingPassword(true);

if (newPassword.length < 8) {
setChangePasswordError('Password must be at least 8 characters long.');
setIsChangingPassword(false);
return;
}
if (newPassword !== confirmPassword) {
setChangePasswordError('New passwords do not match.');
setIsChangingPassword(false);
return;
}
if (newPassword === currentPassword) {
setChangePasswordError('New password must be different from current password.');
setIsChangingPassword(false);
return;
}

try {
const response = await apiClient.post('/admin-password-change', {
currentPassword,
newPassword,
confirmPassword
});

if (!response.success) {
throw new Error(response.error || 'Failed to change password');
}

setChangePasswordSuccess('Password changed successfully!');
setCurrentPassword('');
setNewPassword('');
setConfirmPassword('');
setShowCurrentPassword(false);
setShowNewPassword(false);
setShowConfirmPassword(false);

await logActivity({
action: 'password_changed',
severity: 'info',
details: {
description: 'Password changed successfully'
}
});

setTimeout(() => {
setChangePasswordSuccess('');
}, 3000);
} catch (err) {
setChangePasswordError(err instanceof Error ? err.message : 'Failed to change password');
} finally {
setIsChangingPassword(false);
}
};

// ==========================================
// SETTINGS API
// ==========================================
const fetchSettings = async () => {
// Get token from localStorage directly to ensure it's available
const authToken = localStorage.getItem('adminToken');
if (!authToken) {
console.log('⏭️ fetchSettings: No token found, skipping');
return;
}

setSettingsLoading(true);

try {
const response = await apiClient.get('/admin-settings');

const settings = response.settings || {};
const profile = settings.profile || {};
const security = settings.security || {};
const notifications = settings.notifications || {};
const theme = settings.theme || {};

setUserProfile((current) => ({
...current,
name: typeof profile.name === 'string' ? profile.name : current.name,
email: typeof profile.email === 'string' ? profile.email : current.email,
phone: typeof profile.phone === 'string' ? profile.phone : current.phone
}));

setSecuritySettings({
twoFactorEnabled: Boolean(security.twoFactorEnabled),
sessionTimeout: Number(security.sessionTimeout) || 30,
ipWhitelist: Array.isArray(security.ipWhitelist) ? security.ipWhitelist : [],
lastLogin: security.lastLogin ? new Date(security.lastLogin).toLocaleString() : 'No login recorded',
loginHistory: Array.isArray(security.loginHistory) ? security.loginHistory.map((entry: any) => ({
date: entry?.date ? new Date(entry.date).toLocaleString() : '',
ip: entry?.ip || '',
device: entry?.device || ''
})) : []
});

setNotificationPreferences({
emailAlerts: notifications.emailAlerts !== false,
systemNotifications: notifications.systemNotifications !== false,
donationAlerts: notifications.donationAlerts !== false,
contactAlerts: notifications.contactAlerts !== false,
projectUpdates: notifications.projectUpdates !== false
});

setThemeSettings({
mode: ['light', 'dark', 'system'].includes(theme.mode) ? theme.mode : 'light',
primaryColor: typeof theme.primaryColor === 'string' ? theme.primaryColor : '#ed1c24',
accentColor: typeof theme.accentColor === 'string' ? theme.accentColor : '#afe11a'
});
} catch (err) {
console.error('fetchSettings error:', err);
setError(err instanceof Error ? err.message : 'Failed to load admin settings');
} finally {
setSettingsLoading(false);
}
};

// ==========================================
// CONTACTS
// ==========================================
const fetchContacts = async () => {
// Get token from localStorage directly to ensure it's available
const authToken = localStorage.getItem('adminToken');
if (!authToken) {
console.log('⏭️ fetchContacts: No token found, skipping');
return;
}

setLoading(true);
try {
const response = await apiClient.get('/admin-contacts');
setContacts(response.data || []);
setError('');
} catch (err) {
console.error('fetchContacts error:', err);
setError(err instanceof Error ? err.message : 'Failed to load contacts');
setContacts([]);
} finally {
setLoading(false);
}
};

// ==========================================
// UPDATE CONTACT STATUS
// ==========================================
const updateStatus = async (id: string, status: string) => {
try {
const response = await apiClient.put('/admin-update-status', { id, status });
if (!response.success) {
throw new Error(response.error || 'Failed to update status');
}
await fetchContacts();
} catch (err) {
setError(err instanceof Error ? err.message : 'Failed to update status');
}
};

// ==========================================
// FILTERED CONTACTS
// ==========================================
const filteredContacts = filter === 'all'
? contacts
: contacts.filter((c) => c.status === filter);

const counts = {
all: contacts.length,
new: contacts.filter((c) => c.status === 'new').length,
read: contacts.filter((c) => c.status === 'read').length,
replied: contacts.filter((c) => c.status === 'replied').length,
archived: contacts.filter((c) => c.status === 'archived').length
};

// ==========================================
// HANDLE SAVE SETTINGS
// ==========================================
const handleSaveSettings = async () => {
const authToken = localStorage.getItem('adminToken');
if (!authToken) {
setError('Your session has expired. Please login again.');
return;
}

setSettingsSaving(true);
setSettingsSaved(false);

try {
const response = await apiClient.put('/admin-settings', {
profile: userProfile,
security: {
sessionTimeout: securitySettings.sessionTimeout,
ipWhitelist: securitySettings.ipWhitelist
},
notifications: notificationPreferences,
theme: themeSettings
});

if (!response.success) {
throw new Error(response.error || 'Failed to save settings');
}

setSettingsSaved(true);
setError('');
await fetchSettings();

window.setTimeout(() => setSettingsSaved(false), 3000);
} catch (err) {
setError(err instanceof Error ? err.message : 'Failed to save settings');
} finally {
setSettingsSaving(false);
}
};

const addIpAddress = () => {
const ip = ipInput.trim();
if (!ip) return;
if (securitySettings.ipWhitelist.includes(ip)) {
setIpInput('');
return;
}
if (securitySettings.ipWhitelist.length >= 50) {
setError('You can add a maximum of 50 IP addresses.');
return;
}
setSecuritySettings((current) => ({
...current,
ipWhitelist: [...current.ipWhitelist, ip]
}));
setIpInput('');
};

const removeIpAddress = (ip: string) => {
setSecuritySettings((current) => ({
...current,
ipWhitelist: current.ipWhitelist.filter((value) => value !== ip)
}));
};

// ==========================================
// 2FA SETUP HANDLER
// ==========================================
const handleEnable2FA = async () => {
const authToken = localStorage.getItem('adminToken');
if (!authToken) {
setError('Your session has expired. Please login again.');
return;
}

try {
const response = await apiClient.post('/admin-2fa', { action: 'setup' });

if (!response.success) {
throw new Error(response.error || 'Failed to set up 2FA');
}

setTwoFASetup({
qrCode: response.qrCode,
secret: response.secret
});
setTwoFACode('');
setTwoFAMode('setup');
setShow2FAVerification(true);
setError('');
} catch (error) {
setError(error instanceof Error ? error.message : 'Failed to set up 2FA');
}
};

const handleDisable2FA = () => {
setTwoFACode('');
setTwoFASetup(null);
setTwoFAMode('disable');
setShow2FAVerification(true);
};

const handleVerify2FA = async () => {
const authToken = localStorage.getItem('adminToken');
if (!authToken) {
setError('Your session has expired. Please login again.');
return;
}

if (!twoFAMode || twoFAMode === 'login') {
return;
}

const code = twoFACode.trim();
if (!/^\d{6}$/.test(code)) {
setError('Enter the 6-digit authenticator code.');
return;
}

setLoading(true);
setError('');

try {
const response = await apiClient.post('/admin-2fa', {
action: twoFAMode === 'setup' ? 'verify' : 'disable',
token: code
});

if (!response.success) {
throw new Error(response.error || '2FA operation failed');
}

setShow2FAVerification(false);
setTwoFACode('');
setTwoFASetup(null);
setTwoFAMode(null);
await fetchSettings();
} catch (error) {
setError(error instanceof Error ? error.message : '2FA operation failed');
} finally {
setLoading(false);
}
};

// ==========================================
// LOGIN SCREEN
// ==========================================
if (!token) {
return (
<div className="admin-container auth-shell">
<div className="login-spotlight">
<div className="brand-mark">
<div className="brand-mark-inner">
<ShieldCheck size={32} />
</div>
</div>
<div className="spotlight-heading">
<span className="eyebrow">Secure staff portal</span>
<h1>Let's Change Zambia</h1>
</div>
<p className="spotlight-copy">
Access the internal dashboard for programme coordination,
donor activity, and community impact reporting.
</p>
<div className="trust-points">
<div className="trust-item">
<ShieldCheck size={18} />
<span>Encrypted access</span>
</div>
<div className="trust-item">
<HeartHandshake size={18} />
<span>Trusted staff workflows</span>
</div>
<div className="trust-item">
<CheckCircle2 size={18} />
<span>Role-based administration</span>
</div>
</div>
</div>
<div className="login-card">
<div className="login-header">
<div className="login-icon-wrap">
<Shield size={28} className="login-icon" />
</div>
<div>
<p className="login-kicker">Admin access</p>
<h2>Welcome back</h2>
</div>
</div>
{authMode === 'login' ? (
<form onSubmit={handleSubmit(handleLogin)} className="login-form" noValidate>
<div className="form-group">
<label htmlFor="username">
<User size={16} />
Username
</label>
<input
id="username"
{...register('username')}
type="text"
autoComplete="username"
placeholder="Enter your username"
disabled={loading}
tabIndex={1}
aria-invalid={loginFormErrors.username ? 'true' : 'false'}
/>
{loginFormErrors.username && (
<div className="field-error" role="alert">
{loginFormErrors.username.message}
</div>
)}
</div>
<div className="form-group password-group">
<label htmlFor="password">
<LockKeyhole size={16} />
Password
</label>
<div className="password-input-wrapper">
<input
id="password"
{...register('password')}
type={showPassword ? 'text' : 'password'}
autoComplete="current-password"
placeholder="Enter your password"
disabled={loading}
tabIndex={2}
aria-invalid={loginFormErrors.password ? 'true' : 'false'}
/>
{loginFormErrors.password && (
<div className="field-error" role="alert">
{loginFormErrors.password.message}
</div>
)}
<button type="button" className="toggle-password" onClick={() => setShowPassword((prev) => !prev)} aria-label={showPassword ? 'Hide password' : 'Show password'} title={showPassword ? 'Hide password' : 'Show password'} disabled={loading} tabIndex={3}>
{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
</button>
</div>
</div>
{error && (
<div className="error-message" role="alert" aria-live="polite" tabIndex={-1}>
<AlertCircle size={16} />
Oops! That username and password don't match.
</div>
)}
<div className="login-meta-row">
<label className="remember-me" htmlFor="remember-device" tabIndex={4}>
<input id="remember-device" type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} tabIndex={4} />
<span>Remember this device</span>
</label>
<button type="button" className="text-link-button" onClick={() => { setAuthMode('request-reset'); setError(''); setResetSuccess(''); setResetData({ username: '', token: '', newPassword: '', confirmPassword: '' }); }} tabIndex={5}>
Forgot password?
</button>
</div>
<button type="submit" className="btn-primary login-btn" disabled={loading} tabIndex={6}>
{loading ? <><Loader2 size={18} className="spinner" /> Signing in...</> : <><ArrowRight size={18} /> Access dashboard</>}
</button>
<div className="login-footer-support">
<a href="mailto:admin@letschangezambia.org" className="login-support-link" tabIndex={7}>Need help?</a>
</div>
</form>
) : authMode === 'request-reset' ? (
<form onSubmit={handlePasswordResetRequest} className="reset-form">
<div className="login-header reset-header">
<div className="login-icon-wrap"><Mail size={28} className="login-icon" /></div>
<div><p className="login-kicker">Account recovery</p><h2>Forgot password?</h2></div>
</div>
<p className="reset-intro">Enter your administrator username and we will email a secure password reset link to the registered email address.</p>
<div className="form-group">
<label htmlFor="reset-username"><User size={16} /> Username</label>
<input id="reset-username" type="text" value={resetData.username} onChange={(e) => setResetData({ ...resetData, username: e.target.value })} autoComplete="username" required placeholder="Enter your username" disabled={loading} autoFocus />
</div>
{error && <div className="error-message" role="alert"><AlertCircle size={16} />{error}</div>}
{resetSuccess && <div className="success-message" role="status"><CheckCircle2 size={16} />{resetSuccess}</div>}
<div className="reset-actions">
<button type="submit" className="btn-primary login-btn" disabled={loading}>
{loading ? <><Loader2 size={18} className="spinner" /> Sending...</> : <><Mail size={18} /> Email reset link</>}
</button>
<button type="button" className="btn-secondary auth-return" onClick={() => { setAuthMode('login'); setError(''); setResetSuccess(''); }}>Back to login</button>
</div>
</form>
) : (
<form onSubmit={handlePasswordReset} className="reset-form">
<div className="login-header reset-header">
<div className="login-icon-wrap"><ShieldCheck size={28} className="login-icon" /></div>
<div><p className="login-kicker">Secure access</p><h2>Reset password</h2></div>
</div>
<p className="reset-intro">Create a new secure password. Your reset link is being used automatically.</p>
<div className="form-group">
<label htmlFor="new-password"><LockKeyhole size={16} /> New password</label>
<input id="new-password" type="password" value={resetData.newPassword} onChange={(e) => setResetData({ ...resetData, newPassword: e.target.value })} autoComplete="new-password" required placeholder="Create a new password" disabled={loading} autoFocus />
</div>
<div className="form-group">
<label htmlFor="confirm-password"><LockKeyhole size={16} /> Confirm password</label>
<input id="confirm-password" type="password" value={resetData.confirmPassword} onChange={(e) => setResetData({ ...resetData, confirmPassword: e.target.value })} autoComplete="new-password" required placeholder="Confirm your new password" disabled={loading} />
</div>
{error && <div className="error-message" role="alert"><AlertCircle size={16} />{error}</div>}
{resetSuccess && <div className="success-message" role="status"><CheckCircle2 size={16} />{resetSuccess}</div>}
<div className="reset-actions">
<button type="submit" className="btn-primary login-btn" disabled={loading || !resetData.token}>
{loading ? <><Loader2 size={18} className="spinner" /> Updating password...</> : <><ArrowRight size={18} /> Update password</>}
</button>
<button type="button" className="btn-secondary auth-return" onClick={() => { setAuthMode('login'); setError(''); setResetSuccess(''); }}>Back to login</button>
</div>
</form>
)}
</div>
{show2FAVerification && twoFAMode === 'login' && (
<div className="modal-overlay" onClick={() => {
setShow2FAVerification(false);
setTwoFAMode(null);
setIs2FARequired(false);
setTwoFACode('');
setError('');
}}>
<div className="modal" onClick={(e) => e.stopPropagation()}>
<div className="modal-header">
<h2>
<Smartphone size={24} className="modal-icon" />
Two-Factor Authentication Required
</h2>
<button
 type="button"
 className="modal-close"
 onClick={() => {
 setShow2FAVerification(false);
 setTwoFAMode(null);
 setIs2FARequired(false);
 setTwoFACode('');
 setError('');
 }}
 aria-label="Cancel two-factor authentication"
>
<X size={20} />
</button>
</div>
<div className="modal-body">
<p>
Your password was verified. Enter the current 6-digit code from your authenticator app to complete sign-in.
</p>
<div className="form-group">
<label htmlFor="login-2fa-code">Authenticator Code</label>
<input
id="login-2fa-code"
type="text"
inputMode="numeric"
autoComplete="one-time-code"
value={twoFACode}
onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
placeholder="Enter 6-digit code"
maxLength={6}
autoFocus
/>
</div>
{error && (
<div className="error-message" role="alert">
<AlertCircle size={16} />
{error}
</div>
)}
<div className="form-actions">
<button
className="btn-primary"
onClick={handleVerify2FALogin}
disabled={twoFACode.length !== 6 || loading}
>
{loading ? <Loader2 size={18} className="spinner" /> : <ShieldCheck size={18} />}
{loading ? 'Verifying...' : 'Verify & Sign In'}
</button>
<button
className="btn-secondary"
onClick={() => {
setShow2FAVerification(false);
setTwoFAMode(null);
setIs2FARequired(false);
setTwoFACode('');
setError('');
}}
disabled={loading}
>
Back
</button>
</div>
</div>
</div>
</div>
)}
</div>
);
}

// ==========================================
// DASHBOARD
// ==========================================
return (
<div className="admin-container">
{/* Header */}
<div className="admin-header">
<div className="header-left">
<h1>
<LayoutDashboard size={24} className="header-icon" />
Administration Dashboard
</h1>
<span className="header-org">Let's Change Zambia</span>
</div>
<div className="admin-actions">
<span className="admin-user">
<User size={14} />
{user?.username || 'Admin'}
{user?.role === 'super_admin' && <span className="role-badge" style={{ marginLeft: '8px', fontSize: '10px', backgroundColor: '#ed1c24', color: '#fff', padding: '2px 8px', borderRadius: '10px' }}>Super Admin</span>}
</span>
<button
onClick={() => {
/* TODO: Add notifications */
}}
className="icon-btn"
aria-label="Notifications"
title="Notifications"
>
<Bell size={18} />
<span className="notification-dot" />
</button>
<button onClick={() => setActiveTab('settings')} className="icon-btn" aria-label="Settings" title="Settings">
<Settings size={18} />
</button>
<button onClick={() => fetchContacts()} className="refresh-btn">
<RefreshCw size={16} />
Refresh
</button>
<button onClick={handleLogout} className="logout-btn">
<LogOut size={16} />
Logout
</button>
</div>
</div>
{/* Tabs */}
<div className="admin-tabs" role="tablist" aria-label="Admin sections">
<button
className={activeTab === 'contacts' ? 'active' : ''}
onClick={() => setActiveTab('contacts')}
role="tab"
aria-selected={activeTab === 'contacts'}
aria-label="Messages"
>
<MessageSquare size={16} />
Messages ({contacts.length})
</button>
<button
className={activeTab === 'resources' ? 'active' : ''}
onClick={() => setActiveTab('resources')}
role="tab"
aria-selected={activeTab === 'resources'}
aria-label="Resources"
>
<BookOpen size={16} />
Resources
</button>
<button
className={activeTab === 'projects' ? 'active' : ''}
onClick={() => setActiveTab('projects')}
role="tab"
aria-selected={activeTab === 'projects'}
aria-label="Projects"
>
<FolderOpen size={16} />
Projects
</button>
<button
className={activeTab === 'donations' ? 'active' : ''}
onClick={() => setActiveTab('donations')}
role="tab"
aria-selected={activeTab === 'donations'}
aria-label="Donations"
>
<HandCoins size={16} />
Donations
</button>
<button
className={activeTab === 'activity' ? 'active' : ''}
onClick={() => setActiveTab('activity')}
role="tab"
aria-selected={activeTab === 'activity'}
aria-label="Activity Log"
>
<Activity size={16} />
Activity Log
</button>
<button
className={activeTab === 'settings' ? 'active' : ''}
onClick={() => setActiveTab('settings')}
role="tab"
aria-selected={activeTab === 'settings'}
aria-label="Settings"
>
<Settings size={16} />
Settings
</button>
</div>
{/* Contacts Tab */}
{activeTab === 'contacts' && (
<ErrorBoundary>
<>
<div className="filter-bar">
<button className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>
All ({counts.all})
</button>
<button className={filter === 'new' ? 'active' : ''} onClick={() => setFilter('new')}>
New ({counts.new})
</button>
<button className={filter === 'read' ? 'active' : ''} onClick={() => setFilter('read')}>
Read ({counts.read})
</button>
<button className={filter === 'replied' ? 'active' : ''} onClick={() => setFilter('replied')}>
Replied ({counts.replied})
</button>
<button className={filter === 'archived' ? 'active' : ''} onClick={() => setFilter('archived')}>
Archived ({counts.archived})
</button>
</div>
{loading ? (
<div className="loading">Loading...</div>
) : error ? (
<div className="error-message">{error}</div>
) : (
<div className="contacts-table">
{filteredContacts.map((contact) => (
<div key={contact._id} className={`contact-card ${contact.status}`}>
<div className="contact-header">
<div>
<h3>{contact.name}</h3>
<a href={`mailto:${contact.email}`}>{contact.email}</a>
</div>
<div className="contact-meta">
<span className={`status-badge ${contact.status}`}>{contact.status}</span>
<span className="date">{new Date(contact.createdAt).toLocaleDateString()}</span>
</div>
</div>
<div className="contact-message">{contact.message}</div>
<div className="contact-actions">
<select value={contact.status} onChange={(e) => updateStatus(contact._id, e.target.value)}>
<option value="new">New</option>
<option value="read">Read</option>
<option value="replied">Replied</option>
<option value="archived">Archived</option>
</select>
<a href={`mailto:${contact.email}?subject=Re: Your message to Let's Change Zambia`} className="reply-btn">
Reply via Email
</a>
</div>
</div>
))}
{filteredContacts.length === 0 && (
<div className="no-contacts">No contacts found</div>
)}
</div>
)}
</>
</ErrorBoundary>
)}
{/* Resources Tab */}
{activeTab === 'resources' && (
<ErrorBoundary>
<ResourceManager />
</ErrorBoundary>
)}
{/* Projects Tab */}
{activeTab === 'projects' && (
<ErrorBoundary>
<ProjectManager />
</ErrorBoundary>
)}
{/* Donations Tab */}
{activeTab === 'donations' && (
<ErrorBoundary>
<Donations />
</ErrorBoundary>
)}
{/* Activity Log Tab */}
{activeTab === 'activity' && (
<ErrorBoundary>
<ActivityLog />
</ErrorBoundary>
)}
{/* Settings Tab */}
{activeTab === 'settings' && (
<ErrorBoundary>
<div className="settings-container">
<div className="settings-header">
<h2>
<Settings size={24} className="settings-icon" />
Settings
</h2>
<p className="settings-subtitle">
Manage your profile, security, preferences, and password.
</p>
{settingsLoading && (
<div className="form-hint" role="status">
<Loader2 size={14} className="spinner" /> Loading saved settings...
</div>
)}
</div>
<div className="settings-tabs">
<button
className={`settings-tab ${settingsTab === 'profile' ? 'active' : ''}`}
onClick={() => setSettingsTab('profile')}
>
<UserCircle size={16} />
Profile
</button>
<button
className={`settings-tab ${settingsTab === 'security' ? 'active' : ''}`}
onClick={() => setSettingsTab('security')}
>
<ShieldIcon size={16} />
Security
</button>
<button
className={`settings-tab ${settingsTab === 'notifications' ? 'active' : ''}`}
onClick={() => setSettingsTab('notifications')}
>
<Bell size={16} />
Notifications
</button>
<button
className={`settings-tab ${settingsTab === 'theme' ? 'active' : ''}`}
onClick={() => setSettingsTab('theme')}
>
<Palette size={16} />
Theme
</button>
</div>
<div className="settings-content">
{/* Profile Settings */}
{settingsTab === 'profile' && (
<div className="settings-section">
<h3>Profile Information</h3>
<div className="form-group">
<label htmlFor="profile-name"><User size={16} /> Full Name</label>
<input
id="profile-name"
type="text"
value={userProfile.name}
onChange={(e) => setUserProfile({ ...userProfile, name: e.target.value })}
placeholder="Enter your full name"
/>
</div>
<div className="form-group">
<label htmlFor="profile-email"><AtSign size={16} /> Email Address</label>
<input
id="profile-email"
type="email"
value={userProfile.email}
onChange={(e) => setUserProfile({ ...userProfile, email: e.target.value })}
placeholder="Enter your email address"
/>
</div>
<div className="form-group">
<label htmlFor="profile-phone"><Phone size={16} /> Phone Number</label>
<input
id="profile-phone"
type="tel"
value={userProfile.phone}
onChange={(e) => setUserProfile({ ...userProfile, phone: e.target.value })}
placeholder="Enter your phone number"
/>
</div>
<button className="btn-primary" onClick={handleSaveSettings} disabled={settingsSaving || settingsLoading}>
{settingsSaving ? <Loader2 size={16} className="spinner" /> : <Save size={16} />}
{settingsSaving ? 'Saving...' : 'Save Profile'}
</button>
{settingsSaved && (
<div className="success-message" role="status">
<CheckCircle2 size={16} /> Settings saved successfully!
</div>
)}
</div>
)}
{/* Security Settings */}
{settingsTab === 'security' && (
<div className="settings-section">
<h3>Security Settings</h3>
<div className="security-item">
<div className="security-item-header">
<Lock size={20} />
<div>
<h4>Two-Factor Authentication</h4>
<p>Add an extra layer of security to your account.</p>
</div>
</div>
<div className="security-item-actions">
{securitySettings.twoFactorEnabled ? (
<>
<span className="status-badge success"><ShieldCheck size={14} /> Enabled</span>
<button className="btn-secondary" onClick={handleDisable2FA}>
<Unlock size={16} /> Disable 2FA
</button>
</>
) : (
<button className="btn-primary" onClick={handleEnable2FA}>
<Smartphone size={16} /> Enable 2FA
</button>
)}
</div>
</div>
<div className="security-item">
<div className="security-item-header">
<Clock size={20} />
<div>
<h4>Session Timeout</h4>
<p>Automatically log out after inactivity.</p>
</div>
</div>
<div className="security-item-actions">
<select value={securitySettings.sessionTimeout} onChange={(e) => setSecuritySettings({ ...securitySettings, sessionTimeout: parseInt(e.target.value) })}>
<option value="15">15 minutes</option>
<option value="30">30 minutes</option>
<option value="60">1 hour</option>
<option value="120">2 hours</option>
</select>
</div>
</div>
<div className="security-item">
<div className="security-item-header">
<ShieldIcon size={20} />
<div>
<h4>IP Whitelist</h4>
<p>Allow access only from specific IP addresses.</p>
</div>
</div>
<div className="security-item-actions">
<input
type="text"
value={ipInput}
onChange={(e) => setIpInput(e.target.value)}
placeholder="Enter IP address (e.g., 192.168.1.1)"
className="ip-input"
onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addIpAddress(); } }}
/>
<button className="btn-secondary" type="button" onClick={addIpAddress}>Add IP</button>
</div>
{securitySettings.ipWhitelist.length > 0 && (
<div className="ip-list">
{securitySettings.ipWhitelist.map((ip, index) => (
<span key={index} className="ip-tag">
{ip}
<button className="ip-tag-remove" type="button" onClick={() => removeIpAddress(ip)} aria-label={`Remove ${ip}`}>✕</button>
</span>
))}
</div>
)}
</div>
<div className="security-item">
<div className="security-item-header">
<Activity size={20} />
<div>
<h4>Recent Login History</h4>
<p>View recent login activity on your account.</p>
</div>
</div>
<div className="login-history">
{securitySettings.loginHistory.map((entry, index) => (
<div key={index} className="login-history-item">
<div className="login-history-info">
<span className="login-history-date">{entry.date}</span>
<span className="login-history-ip">IP: {entry.ip}</span>
<span className="login-history-device">{entry.device}</span>
</div>
</div>
))}
</div>
</div>
<button className="btn-primary" onClick={handleSaveSettings} disabled={settingsSaving || settingsLoading}>
{settingsSaving ? <Loader2 size={16} className="spinner" /> : <Save size={16} />}
{settingsSaving ? 'Saving...' : 'Save Security Settings'}
</button>
{settingsSaved && (
<div className="success-message" role="status">
<CheckCircle2 size={16} /> Settings saved successfully!
</div>
)}
</div>
)}
{/* Notification Preferences */}
{settingsTab === 'notifications' && (
<div className="settings-section">
<h3>Notification Preferences</h3>
<div className="notification-item">
<label className="notification-label">
<div className="notification-info">
<span className="notification-icon"><MailIcon size={20} /></span>
<div>
<h4>Email Alerts</h4>
<p>Receive email notifications for important events.</p>
</div>
</div>
<input type="checkbox" checked={notificationPreferences.emailAlerts} onChange={(e) => setNotificationPreferences({ ...notificationPreferences, emailAlerts: e.target.checked })} />
</label>
</div>
<div className="notification-item">
<label className="notification-label">
<div className="notification-info">
<span className="notification-icon"><BellRing size={20} /></span>
<div>
<h4>System Notifications</h4>
<p>Receive system updates and maintenance alerts.</p>
</div>
</div>
<input type="checkbox" checked={notificationPreferences.systemNotifications} onChange={(e) => setNotificationPreferences({ ...notificationPreferences, systemNotifications: e.target.checked })} />
</label>
</div>
<div className="notification-item">
<label className="notification-label">
<div className="notification-info">
<span className="notification-icon"><Gift size={20} /></span>
<div>
<h4>Donation Alerts</h4>
<p>Get notified when new donations are received.</p>
</div>
</div>
<input type="checkbox" checked={notificationPreferences.donationAlerts} onChange={(e) => setNotificationPreferences({ ...notificationPreferences, donationAlerts: e.target.checked })} />
</label>
</div>
<div className="notification-item">
<label className="notification-label">
<div className="notification-info">
<span className="notification-icon"><MessageSquareIcon size={20} /></span>
<div>
<h4>Contact Alerts</h4>
<p>Be notified when someone submits a contact form.</p>
</div>
</div>
<input type="checkbox" checked={notificationPreferences.contactAlerts} onChange={(e) => setNotificationPreferences({ ...notificationPreferences, contactAlerts: e.target.checked })} />
</label>
</div>
<div className="notification-item">
<label className="notification-label">
<div className="notification-info">
<span className="notification-icon"><Clipboard size={20} /></span>
<div>
<h4>Project Updates</h4>
<p>Get updates on project changes and milestones.</p>
</div>
</div>
<input type="checkbox" checked={notificationPreferences.projectUpdates} onChange={(e) => setNotificationPreferences({ ...notificationPreferences, projectUpdates: e.target.checked })} />
</label>
</div>
<button className="btn-primary" onClick={handleSaveSettings} disabled={settingsSaving || settingsLoading}>
{settingsSaving ? <Loader2 size={16} className="spinner" /> : <Save size={16} />}
{settingsSaving ? 'Saving...' : 'Save Notification Preferences'}
</button>
{settingsSaved && (
<div className="success-message" role="status">
<CheckCircle2 size={16} /> Settings saved successfully!
</div>
)}
</div>
)}
{/* Theme Settings */}
{settingsTab === 'theme' && (
<div className="settings-section">
<h3>Theme Preferences</h3>
<div className="theme-options">
<button className={`theme-option ${themeSettings.mode === 'light' ? 'active' : ''}`} onClick={() => setThemeSettings({ ...themeSettings, mode: 'light' })}>
<Sun size={24} />
<span>Light</span>
</button>
<button className={`theme-option ${themeSettings.mode === 'dark' ? 'active' : ''}`} onClick={() => setThemeSettings({ ...themeSettings, mode: 'dark' })}>
<Moon size={24} />
<span>Dark</span>
</button>
<button className={`theme-option ${themeSettings.mode === 'system' ? 'active' : ''}`} onClick={() => setThemeSettings({ ...themeSettings, mode: 'system' })}>
<Monitor size={24} />
<span>System</span>
</button>
</div>
<div className="form-group">
<label htmlFor="primary-color">Primary Color</label>
<div className="color-picker">
<input id="primary-color" type="color" value={themeSettings.primaryColor} onChange={(e) => setThemeSettings({ ...themeSettings, primaryColor: e.target.value })} />
<span className="color-hex">{themeSettings.primaryColor}</span>
</div>
</div>
<div className="form-group">
<label htmlFor="accent-color">Accent Color</label>
<div className="color-picker">
<input id="accent-color" type="color" value={themeSettings.accentColor} onChange={(e) => setThemeSettings({ ...themeSettings, accentColor: e.target.value })} />
<span className="color-hex">{themeSettings.accentColor}</span>
</div>
</div>
<button className="btn-primary" onClick={handleSaveSettings} disabled={settingsSaving || settingsLoading}>
{settingsSaving ? <Loader2 size={16} className="spinner" /> : <Save size={16} />}
{settingsSaving ? 'Saving...' : 'Save Theme Settings'}
</button>
{settingsSaved && (
<div className="success-message" role="status">
<CheckCircle2 size={16} /> Settings saved successfully!
</div>
)}
</div>
)}
{/* Change Password - part of Security Settings */}
{settingsTab === 'security' && (
<div className="security-item password-security-item">
<div className="security-item-header">
<KeyRound size={20} />
<div>
<h4>Change Password</h4>
<p>Update your password to keep your administrator account secure.</p>
</div>
</div>
<div className="settings-section">
<p className="settings-subtitle">Update your password to keep your account secure.</p>
<form onSubmit={handleChangePassword}>
<div className="form-group">
<label htmlFor="current-password-settings"><LockKeyhole size={16} /> Current Password</label>
<div className="password-input-wrapper">
<input
id="current-password-settings"
name="current-password"
type={showCurrentPassword ? 'text' : 'password'}
value={currentPassword}
onChange={(e) => setCurrentPassword(e.target.value)}
required
placeholder="Enter your current password"
disabled={isChangingPassword}
autoComplete="current-password"
/>
<button
type="button"
className="toggle-password"
onClick={() => setShowCurrentPassword((prev) => !prev)}
aria-label={showCurrentPassword ? 'Hide current password' : 'Show current password'}
title={showCurrentPassword ? 'Hide current password' : 'Show current password'}
disabled={isChangingPassword}
>
{showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
</button>
</div>
</div>
<div className="form-group">
<label htmlFor="new-password-settings"><LockKeyhole size={16} /> New Password</label>
<div className="password-input-wrapper">
<input
id="new-password-settings"
name="new-password"
type={showNewPassword ? 'text' : 'password'}
value={newPassword}
onChange={(e) => setNewPassword(e.target.value)}
required
placeholder="Create a new password (8+ characters)"
disabled={isChangingPassword}
autoComplete="new-password"
/>
<button
type="button"
className="toggle-password"
onClick={() => setShowNewPassword((prev) => !prev)}
aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
title={showNewPassword ? 'Hide new password' : 'Show new password'}
disabled={isChangingPassword}
>
{showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
</button>
</div>
<small className="form-hint">Password must be at least 8 characters long.</small>
</div>
<div className="form-group">
<label htmlFor="confirm-password-settings"><LockKeyhole size={16} /> Confirm New Password</label>
<div className="password-input-wrapper">
<input
id="confirm-password-settings"
name="confirm-password"
type={showConfirmPassword ? 'text' : 'password'}
value={confirmPassword}
onChange={(e) => setConfirmPassword(e.target.value)}
required
placeholder="Confirm your new password"
disabled={isChangingPassword}
autoComplete="new-password"
/>
<button
type="button"
className="toggle-password"
onClick={() => setShowConfirmPassword((prev) => !prev)}
aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
title={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
disabled={isChangingPassword}
>
{showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
</button>
</div>
</div>
{changePasswordError && (
<div className="error-message" role="alert">
<AlertCircle size={16} /> {changePasswordError}
</div>
)}
{changePasswordSuccess && (
<div className="success-message" role="status">
<CheckCircle2 size={16} /> {changePasswordSuccess}
</div>
)}
<div className="form-actions">
<button type="submit" className="btn-primary" disabled={isChangingPassword}>
{isChangingPassword ? (
<><Loader2 size={18} className="spinner" /> Updating...</>
) : (
<><KeyRound size={18} /> Update Password</>
)}
</button>
</div>
</form>
</div>
</div>
)}
</div>
</div>
</ErrorBoundary>
)}
{/* ==========================================
2FA VERIFICATION MODAL
========================================== */}
{show2FAVerification && twoFAMode !== 'login' && (
<div className="modal-overlay" onClick={() => {
setShow2FAVerification(false);
setTwoFAMode(null);
setTwoFACode('');
setTwoFASetup(null);
}}>
<div className="modal" onClick={(e) => e.stopPropagation()}>
<div className="modal-header">
<h2>
<Smartphone size={24} className="modal-icon" />
{twoFAMode === 'setup' ? 'Set Up Two-Factor Authentication' : 'Disable Two-Factor Authentication'}
</h2>
<button
 type="button"
 className="modal-close"
 onClick={() => {
 setShow2FAVerification(false);
 setTwoFAMode(null);
 setTwoFACode('');
 setTwoFASetup(null);
 }}
 aria-label="Close 2FA dialog"
>
<X size={20} />
</button>
</div>
<div className="modal-body">
{twoFAMode === 'setup' && twoFASetup ? (
<>
<p>Scan this QR code with your authenticator app, then enter the current 6-digit code to confirm setup.</p>
<div style={{ textAlign: 'center', margin: '20px 0' }}>
<img src={twoFASetup.qrCode} alt="QR code for two-factor authentication setup" style={{ maxWidth: 220, width: '100%', height: 'auto' }} />
</div>
<p className="form-hint">If you cannot scan the QR code, enter this secret manually:</p>
<div className="ip-tag" style={{ wordBreak: 'break-all' }}>{twoFASetup.secret}</div>
</>
) : (
<p>Enter the current 6-digit code from your authenticator app to disable 2FA. The server will verify the code before changing your account security state.</p>
)}
<div className="form-group">
<label htmlFor="2fa-management-code">Verification Code</label>
<input
id="2fa-management-code"
type="text"
inputMode="numeric"
autoComplete="one-time-code"
value={twoFACode}
onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
placeholder="Enter 6-digit code"
maxLength={6}
autoFocus
/>
</div>
{error && (
<div className="error-message" role="alert">
<AlertCircle size={16} /> {error}
</div>
)}
<div className="form-actions">
<button className="btn-primary" onClick={handleVerify2FA} disabled={twoFACode.length !== 6 || loading}>
{loading ? <Loader2 size={18} className="spinner" /> : <ShieldCheck size={18} />}
{loading ? 'Verifying...' : twoFAMode === 'setup' ? 'Verify & Enable' : 'Verify & Disable'}
</button>
<button className="btn-secondary" onClick={() => {
setShow2FAVerification(false);
setTwoFAMode(null);
setTwoFACode('');
setTwoFASetup(null);
}} disabled={loading}>
Cancel
</button>
</div>
</div>
</div>
</div>
)}
</div>
);
};

export default Admin;